import { Button, CheckBox, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import CheckboxDropDown from '../../components/CheckboxDropDown/CheckboxDropDown';
import Grid from '../../components/Grid/Grid';
import { Paging } from '../../components/Paging/Paging';
import SearchBarExtended from '../../components/SearchBarExtended/SearchBarExtended';
import { useStore } from '../../hooks/use-store';
import './Audit.scss';
import { SubRowRenderer } from './renderers';

const MAX_AUDIT_ID_LENGTH = 10;

const Audit = observer(() => {
  const { auditStore: store } = useStore();
  const {
    loadingEvents,
    events,
    sorting,
    paging,
    expandedRowIds,
    availableTypes,
    pageNumber,
    minAuditId,
    isFailuresOnly,
  } = store;
  const navigate = useNavigate();

  useEffect(() => {
    void store.init();
    return () => store.close();
  }, [store]);

  // @ts-ignore
  const updatePaging = (value: number) => {
    store.updatePagingOffset(value);
    runInAction(() => (store.pageNumber = value));
  };

  const onPageClick = (newPageNumber: number) => {
    updatePaging(newPageNumber);
    void store.fetchAuditEvents();
  };

  const onSortingChanged = () => {
    void store.fetchAuditEvents();
  };

  const onFailuresChanged = (value: boolean) => {
    runInAction(() => (store.isFailuresOnly = value));
    void store.fetchAuditEvents();
  };

  const onAuditIdChange = (value: string) => {
    if (value && (!/^[1-9]\d*$/.test(value) || value.length > MAX_AUDIT_ID_LENGTH)) {
      return;
    }
    runInAction(() => (store.minAuditId = value));
  };

  const onSelectedTypesChange = (types: string[]) => {
    runInAction(() => (store.selectedTypes = types));
    updatePaging(1);
    void store.fetchAuditEvents();
  };

  return (
    <div className='audit-wrapper'>
      <div className='spot-typography__heading--level-2'>Audit</div>
      <div className='page-top-actions'>
        <div>
          <SearchBarExtended
            aria-label='Label Search'
            disabled={loadingEvents}
            onChange={e => runInAction(() => (store.searchText = e.target.value))}
            maxLength={40}
            value=''
            placeholder='Search User Email, Event, Type, SAP ID, Agent Name'
            onSearchClick={() => void store.fetchAuditEvents()}
          />
          <CheckBox
            id='failures-only'
            label='Show Failures Only'
            aria-label='Failures Only'
            className='failures-only'
            disabled={loadingEvents}
            checked={isFailuresOnly}
            onChange={() => onFailuresChanged(!isFailuresOnly)}
          />
          <div className='jump-to-audit-id-wrapper'>
            <TextBox
              id='jump-to-audit-id'
              className='jump-to-audit-id-text-box'
              disabled={loadingEvents}
              value={minAuditId}
              onChange={e => onAuditIdChange(e.target.value)}
              placeholder='Start at ID'
            />
          </div>
          <div className='events-selector'>
            <CheckboxDropDown
              isDisabled={loadingEvents}
              options={availableTypes}
              onChange={onSelectedTypesChange}
            />
          </div>
        </div>
        <Button
          variant='primary'
          onClick={() => void store.fetchAuditEvents()}
          disabled={loadingEvents}
        >
          Refresh
        </Button>
      </div>

      <div className='audit-table'>
        <Grid
          id='audit-grid'
          loading={loadingEvents}
          disabled={loadingEvents}
          columns={store.colDefs}
          data={events}
          sorting={sorting}
          onSortingChanged={onSortingChanged}
          noRowsText='No data'
          rowIdField='id'
          expandedRowIds={expandedRowIds}
          subrowRenderer={SubRowRenderer}
        />
      </div>
      <div className='audit-table-pagination'>
        <Paging
          variant='complex'
          count={Math.ceil(paging.totalEventsCount / paging.pageSize)}
          maxVisible={paging.maxPagesToDisplay}
          currentPage={pageNumber}
          onPageChange={onPageClick}
          disabled={loadingEvents}
        />
      </div>
      <div className='page-bottom-actions'>
        <Button className='back' variant='link' size='small' onClick={() => void navigate(-1)}>
          Back
        </Button>
        <div className='main-buttons'>
          <Button variant='primary' size='small' onClick={() => void navigate('/')}>
            Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
});

export default Audit;
